import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { ArrowRightIcon } from 'lucide-react';
interface PopularToursProps {
  onNavigate?: (page: string) => void;
}
const tours = [{
  title: 'Everest Base Camp Luxury Trek',
  image: 'https://images.unsplash.com/photo-1516302752625-fcc3c50ae61f?w=900&q=80',
  alt: 'Mount Everest Base Camp trek through the Himalayas',
  days: '14 Days',
  description: "Trek to the foot of the world's highest peak with luxury lodges, gourmet dining, and personal Sherpa guides.",
  price: 'From $4,500',
  page: 'everest-trek'
}, {
  title: 'Annapurna Circuit Premium',
  image: 'https://images.unsplash.com/photo-1585409677983-0f6c41ca9c3b?w=900&q=80',
  alt: 'Annapurna mountain range panoramic view',
  days: '12 Days',
  description: 'Circle the Annapurna massif with premium teahouse stays, helicopter transfers, and cultural immersion.',
  price: 'From $3,800'
}, {
  title: 'Chitwan Luxury Safari',
  image: 'https://images.unsplash.com/photo-1574950578143-858c6fc139d8?w=900&q=80',
  alt: 'Wildlife safari in Chitwan National Park',
  days: '5 Days',
  description: 'Encounter rhinos, tigers, and exotic birds from the comfort of a five-star jungle lodge in Chitwan.',
  price: 'From $2,200'
}, {
  title: 'Upper Mustang Expedition',
  image: 'https://images.unsplash.com/photo-1503220317375-aaad61436b1b?w=900&q=80',
  alt: 'Remote Upper Mustang landscape expedition',
  days: '10 Days',
  description: 'Explore the forbidden kingdom of Lo Manthang with exclusive access, luxury camping, and ancient monasteries.',
  price: 'From $5,200'
}, {
  title: 'Langtang Valley Trek',
  image: 'https://images.unsplash.com/photo-1486911278844-a81c5267e227?w=900&q=80',
  alt: 'Langtang Valley pristine mountain landscape',
  days: '8 Days',
  description: 'Discover the "Valley of Glaciers" with pristine forests, Tamang culture, and stunning mountain panoramas.',
  price: 'From $2,800'
}, {
  title: 'Kathmandu Heritage Tour',
  image: 'https://images.unsplash.com/photo-1558799401-1dcba79834c2?w=900&q=80',
  alt: 'Ancient temples and heritage sites of Kathmandu',
  days: '4 Days',
  description: 'Explore UNESCO World Heritage sites, ancient temples, and vibrant markets with private guides and luxury stays.',
  price: 'From $1,500'
}];
export function PopularTours({
  onNavigate
}: PopularToursProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.7,
        delay: i * 0.12,
        ease: [0.22, 1, 0.36, 1]
      }
    })
  };
  return <section id="trips" className="w-full bg-[#0A3D3D] py-24 sm:py-32" ref={ref} data-id="element-328">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-329">
        {/* Section Header */}
        <div className="text-center mb-16" data-id="element-330">
          <motion.div initial={{
          opacity: 0
        }} animate={isInView ? {
          opacity: 1
        } : {}} transition={{
          duration: 0.8,
          ease: [0.22, 1, 0.36, 1]
        }} className="flex items-center justify-center gap-3 mb-6" data-id="element-331">
            <span className="text-[#D4A853]/40 text-xs" data-id="element-332">◆</span>
            <motion.div initial={{
            scaleX: 0
          }} animate={isInView ? {
            scaleX: 1
          } : {}} transition={{
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1]
          }} className="w-16 h-0.5 bg-[#D4A853] origin-center" data-id="element-333" />
            <span className="text-[#D4A853]/40 text-xs" data-id="element-334">◆</span>
          </motion.div>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.1
        }} className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" data-id="element-335">
            Popular Luxury Tours for 2026/2027
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 16
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-white/60 text-lg max-w-xl mx-auto font-light" data-id="element-336">
            Expertly curated itineraries through Nepal's most breathtaking
            landscapes
          </motion.p>
        </div>

        {/* Tour Cards — 2 per row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8" data-id="element-337">
          {tours.map((tour, i) => <motion.article key={tour.title} custom={i} variants={cardVariants} initial="hidden" animate={isInView ? 'visible' : 'hidden'} onClick={() => tour.page && onNavigate?.(tour.page)} className="group bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-xl transition-all duration-500 cursor-pointer" data-id="element-338">
              <div className="relative overflow-hidden aspect-[16/9]" data-id="element-339">
                <img src={tour.image} alt={tour.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" data-id="element-340" />
                <span className="absolute top-4 left-4 px-3 py-1 bg-[#D4A853] text-slate-900 text-xs font-semibold rounded-full" data-id="element-341">
                  {tour.days}
                </span>
              </div>
              <div className="p-6 sm:p-7" data-id="element-342">
                <h3 className="font-playfair text-xl sm:text-2xl font-semibold text-slate-900 mb-2 group-hover:text-teal-700 transition-colors" data-id="element-343">
                  {tour.title}
                </h3>
                <p className="text-slate-500 text-sm leading-relaxed mb-5" data-id="element-344">
                  {tour.description}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-slate-100" data-id="element-345">
                  <span className="text-teal-700 text-lg font-semibold" data-id="element-346">
                    {tour.price}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-400 group-hover:text-teal-700 transition-colors" data-id="element-347">
                    View Details
                    <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-348" />
                  </span>
                </div>
              </div>
            </motion.article>)}
        </div>
      </div>
    </section>;
}